// services/http.js
// The single axios instance every src/services/* module should use.
//
// - Attaches the JWT from localStorage on every request. The backend's passport
//   strategy reads the header with ExtractJwt.fromHeader('authorization'), i.e.
//   the RAW token — no "Bearer " prefix. Do not add one.
// - On a 401 from an authenticated endpoint, clears the session and sends the
//   user to #/sign-in (HashRouter). Auth endpoints themselves are exempt: a 401
//   from /login is "wrong password", not "session expired".
// - Never sets axios.defaults.* — a global default header leaks the token to
//   every third-party host the app calls (geolocation, legacy backend, ...).
import axios from 'axios';
import jwtAuthService from './jwtAuthService';
import { invalidateForWrite } from '../ui/data/invalidation';

export const API_BASE = process.env.REACT_APP_API_BASEURL || 'http://localhost:3005';

// Endpoints whose 401 means "bad credentials", not "expired session".
//
// Same METHOD + path shape as PUBLIC_ENDPOINTS below (`method: '*'` = any):
// a plain substring list let `POST /api/share` / `POST /api/share/revoke-all`
// slip through the exemption, so an expired session on those showed an error
// instead of bouncing to sign-in. Only the public `GET /api/share/:token` is
// meant to be exempt.
const AUTH_ENDPOINTS = [
  { method: '*', match: (url) => /\/login(\?|$)/.test(url) },
  { method: '*', match: (url) => /\/register(\?|$)/.test(url) },
  { method: '*', match: (url) => /\/verify(\?|$)/.test(url) },
  { method: '*', match: (url) => /\/tempPassword(\?|$)/.test(url) },   // forgot password, step 1 (emails a reset link)
  { method: '*', match: (url) => /\/resetPassword(\?|$)/.test(url) },  // forgot password, step 2 ({ token, newPassword })
  // NOT '/changeOfPassword': it sits behind passportJWT now (api Wave 3-C), so
  // a 401 there is an expired session — a wrong current password is a 400.
  { method: '*', match: (url) => /\/auth\/google(\?|$)/.test(url) },
  { method: 'get', match: (url) => /\/api\/share\/[^/?]+(\?|$)/.test(url) }, // public share view (see PUBLIC_ENDPOINTS)
  { method: 'get', match: (url) => /\/api\/leaderboard(\/[^/?]+)?(\?|$)/.test(url) }, // public OOS leaderboard + published run
];

const isAuthEndpoint = (config = {}) => {
  const url = config.url || '';
  const method = String(config.method || 'get').toLowerCase();
  return AUTH_ENDPOINTS.some((p) => (p.method === '*' || p.method === method) && p.match(url));
};

// Endpoints that must be called WITHOUT an Authorization header.
//
// `GET /api/share/:token` (crypto-backend-api routes/share.js) is deliberately
// unauthenticated: it is opened by whoever was sent the link, who is usually not
// a user of this app at all. Sending the viewer's own JWT to it would leak a
// live session token into a page that has no need for it — and would make the
// page behave differently for a signed-in viewer than for the recipient it was
// built for, which is exactly the class of bug that makes "it works for me"
// share links.
//
// Matched on METHOD + path, not path alone: `POST /api/share/revoke-all` also
// contains `/api/share/` and very much does need the header.
//
// `GET /api/leaderboard` and `GET /api/leaderboard/:backtestId` (routes/
// experiments.js) are public for the same reason: the OOS leaderboard and a
// published run's page are meant to be linkable to anyone. The write side
// (`POST/DELETE /api/backtest/:id/publish`) is authenticated and does not match.
const PUBLIC_ENDPOINTS = [
  { method: 'get', match: (url) => /\/api\/share\/[^/]+$/.test(url) },
  { method: 'get', match: (url) => /\/api\/leaderboard(\/[^/?]+)?(\?|$)/.test(url) },
];

const isPublicEndpoint = (config = {}) => {
  const url = config.url || '';
  const method = String(config.method || 'get').toLowerCase();
  return PUBLIC_ENDPOINTS.some((p) => p.method === method && p.match(url));
};

const http = axios.create({ baseURL: API_BASE });

// Two call shapes are in use: `http.get('/api/x')` (joined onto baseURL) and
// `http.get(`${API_BASE}/api/x`)` (already prefixed). With an absolute
// API_BASE axios leaves the second shape alone, but the production value is a
// same-origin PATH ('/api' — NGINX strips it), and axios joins a path onto a
// path: '/api' + '/api/api/x' = '/api/api/api/x'. A url that already carries
// the prefix is therefore sent as-is. Every prefixed call site is
// `${API_BASE}/api/...` (all backend routes live under /api), and that is the
// test — `startsWith(API_BASE + '/')` alone cannot tell the relative
// '/api/changeOfPassword' from a prefixed url when API_BASE is '/api'.
const alreadyPrefixed = (url) =>
  Boolean(API_BASE) && typeof url === 'string' &&
  (url === `${API_BASE}/api` || url.startsWith(`${API_BASE}/api/`) || url.startsWith(`${API_BASE}/api?`));

http.interceptors.request.use((config) => {
  if (alreadyPrefixed(config.url)) config.baseURL = '';
  if (isPublicEndpoint(config)) {
    if (config.headers) delete config.headers.Authorization;
    return config;
  }
  const token = localStorage.getItem('token');
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) config.headers.Authorization = token;
  }
  return config;
});

let redirecting = false;

http.interceptors.response.use(
  (res) => {
    // A successful write marks the cached reads it affects as stale
    // (ui/data/invalidation.js), whichever page made it. Reads pass through.
    try { invalidateForWrite(res && res.config); } catch (_) { /* never fail a request over the cache */ }
    return res;
  },
  (err) => {
    const status = err?.response?.status;
    if (status === 401 && !isAuthEndpoint(err?.config) && !redirecting) {
      redirecting = true;
      try {
        // Clears localStorage auth keys + fires 'session-expired' (SessionWatcher
        // redirects when on a /profile route).
        jwtAuthService.handleSessionExpired();
      } catch (_) { /* ignore */ }
      if (!window.location.hash.startsWith('#/sign-in')) {
        window.location.hash = '#/sign-in';
      }
      setTimeout(() => { redirecting = false; }, 1000);
    }
    return Promise.reject(err);
  }
);

export default http;
